package one.healthium.insights.ui.insights

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.unit.dp
import loco.INSIGHTS_GREETING
import loco.INSIGHTS_GREETING_NAME_FALLBACK
import one.healthium.insights.domain.content.ContentOptions
import one.healthium.insights.domain.models.ActionModel
import one.healthium.insights.domain.models.SavedContentModel
import one.healthium.insights.ui.components.ContentRenderer
import one.healthium.insights.ui.insights.components.InsightTabs
import one.healthium.insights.ui.insights.components.InsightTabsContainer
import one.healthium.insights.ui.insights.components.SavedContentList
import one.healthium.ui.common.message.AlertUiMessageHandler
import one.healthium.ui.compose.components.AppLoadingScreen
import one.healthium.ui.compose.components.AppScaffold
import one.healthium.ui.compose.platform.PlatformColorsEffect
import one.healthium.ui.compose.theme.AppTheme
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel

interface InsightsEventDelegate {
    fun onActionClicked(action: ActionModel)
    fun onContentClicked(options: ContentOptions)
}

@Composable
fun InsightsScreen(
    eventDelegate: InsightsEventDelegate
) {
    val viewModel = koinViewModel<InsightsViewModel>()
    val state by viewModel.state.collectAsState()

    PlatformColorsEffect(lightStatusBar = false)

    InsightScreenContent(
        state = state,
        onActionClicked = eventDelegate::onActionClicked,
        onSavedContentClicked = { eventDelegate.onContentClicked(it.contentOptions) },
    )

    AlertUiMessageHandler(viewModel.messages)
}

@Composable
internal fun InsightScreenContent(
    state: InsightsUiState,
    onActionClicked: (ActionModel) -> Unit,
    onSavedContentClicked: (SavedContentModel) -> Unit,
) {
    AppScaffold(
        modifier = Modifier.testTag("insights_screen"),
        topBar = {
            Text(
                modifier = Modifier
                    .statusBarsPadding()
                    .padding(horizontal = 16.dp, vertical = 12.dp),
                text = stringResource(
                    resource = loco.Res.string.INSIGHTS_GREETING,
                    state.user?.profile?.name ?: stringResource(loco.Res.string.INSIGHTS_GREETING_NAME_FALLBACK)
                ),
                style = AppTheme.textStyles.secondary_17_500,
                color = AppTheme.colors.text400,
            )
        },
    ) {
        Box(
            modifier = Modifier.padding(it)
        ) {
            var currentTab by rememberSaveable { mutableStateOf(InsightTabs.All) }

            InsightTabsContainer(
                currentTab = currentTab,
                onTabClicked = { tab -> currentTab = tab }
            ) { tab ->
                when (tab) {
                    InsightTabs.All -> {
                        if (state.insightsScreen != null) {
                            ContentRenderer(
                                contentPadding = PaddingValues(bottom = 90.dp),
                                content = state.insightsScreen.content,
                                onActionClicked = onActionClicked,
                                isMetrics = state.isMetrics,
                            )
                        }
                    }

                    InsightTabs.Recent -> {
                        if (state.recentlyUiData != null) {
                            SavedContentList(
                                uiData = state.recentlyUiData,
                                onClick = onSavedContentClicked,
                                onEmptyStateClicked = {
                                    currentTab = InsightTabs.All
                                },
                            )
                        }
                    }

                    InsightTabs.Favorites -> {
                        if (state.favoritesUiData != null) {
                            SavedContentList(
                                uiData = state.favoritesUiData,
                                onClick = onSavedContentClicked,
                                onEmptyStateClicked = {
                                    currentTab = InsightTabs.All
                                },
                            )
                        }
                    }
                }
            }

            AppLoadingScreen(visible = state.loading)
        }
    }
}
